import { defaultStringifySearch, useRouter, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * A simple list's view, kept in the address so a colleague opening the link sees the same (06-qol §3): which tab,
 * which filter pill, the search, and the row open in the panel. Read loosely: the routes declare no search schema,
 * so anything odd in the address reads as "not set" rather than breaking the page.
 */
export interface ListAddress {
  readonly tab?: string;
  readonly show?: string;
  readonly q?: string;
  readonly open?: string;
  /** A list's other order (Pools and tags' "Fewest people first"). */
  readonly sort?: string;
}

const KEYS: readonly (keyof ListAddress)[] = ['tab', 'show', 'q', 'open', 'sort'];
const MAX_LENGTH = 200;

export function readListAddress(raw: Readonly<Record<string, unknown>>): ListAddress {
  const out: Record<string, string> = {};
  for (const key of KEYS) {
    // The router reads a search that looks like a number (?q=123) as one.
    const given = raw[key];
    const value = typeof given === 'number' ? String(given) : given;
    if (typeof value === 'string' && value !== '' && value.length <= MAX_LENGTH) out[key] = value;
  }
  return out;
}

/** One of `options`, or the fallback when the address holds anything else. */
export function oneOf<T extends string>(value: string | undefined, options: readonly T[], fallback: T): T {
  return options.find((option) => option === value) ?? fallback;
}

/** The view, and a setter that replaces the address: Back leaves the page rather than stepping through filters. */
export function useListAddress(path: string): readonly [ListAddress, (next: Partial<ListAddress>) => void] {
  const router = useRouter();
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const address = readListAddress(raw);
  const set = useCallback(
    (next: Partial<ListAddress>): void => {
      const merged: Record<string, string> = {};
      const current = readListAddress(router.state.location.search as Readonly<Record<string, unknown>>);
      for (const key of KEYS) {
        const value = key in next ? next[key] : current[key];
        if (value !== undefined && value !== '') merged[key] = value;
      }
      router.history.replace(`${path}${defaultStringifySearch(merged)}`);
    },
    [router, path],
  );
  return [address, set];
}
